// DTO de `GET /api/supervisor/resumen-hora` (S9 — resumen hora a hora por línea).
//
// Pivote tipo Excel del cliente: columnas = líneas del scope del supervisor,
// filas = franjas horarias del día, celda = cantidad producida. Incluye una
// columna sintética TIENDAS que suma las corridas del subsistema ortogonal
// (`registros_tienda`), ubicadas en la franja de su `hora_inicio` (regla de
// negocio confirmada: "por hora de inicio", igual que el Excel del cliente).
//
// Scope gobernado por `usuarios_supervisor_plantas` (misma política que S1).
// El backend arma columnas/filas ya ordenadas y calcula todos los totales; el
// frontend solo renderiza — NO recalcula ni re-ordena.
import { z } from 'zod';
import { unidadMedidaSchema } from './unidad_medida.js';
/**
 * Una columna del pivote.
 *   - `key`: identificador estable para mapear celdas. Para líneas normales es
 *     el `linea_id`; para la columna agregada de tiendas es el literal
 *     `'tiendas'`.
 *   - `es_tienda`: marca la columna sintética de tiendas (render diferenciado).
 */
export const supervisorResumenColumnaSchema = z.object({
    key: z.string(),
    nombre: z.string(),
    es_tienda: z.boolean(),
    total: z.number(),
    /**
     * Cantidad NO validada de la columna (parte del `total` con
     * `validado_at IS NULL`). > 0 → la columna tiene bloques pendientes que NO
     * saldrán en la foto. En el payload de la foto (`solo_validados`) es 0.
     */
    pendiente: z.number(),
    /**
     * Meta acumulada de la línea en el turno (`vw_kpis_linea_turno.meta_acumulada`
     * agrupada por línea — la MISMA fuente cuya suma es `meta_total`, así las
     * filas Meta/Cumplimiento de la foto jamás divergen de la tarjeta KPI).
     * `null` si la línea no aparece en la vista de KPIs; `0` si aparece pero no
     * tiene capacidad estándar configurada — en ambos casos el FE muestra «—».
     */
    meta: z.number().nullable(),
});
/**
 * Una fila del pivote (una franja horaria).
 *   - `franja`: etiqueta lista para mostrar, "06:00 - 07:00".
 *   - `franja_inicio`: "06:00" — orden canónico (el backend ya ordenó).
 *   - `valores`: mapa `columna.key → cantidad`. Una columna ausente en el mapa
 *     significa 0 (sin producción en esa franja). El FE usa `?? 0`.
 */
export const supervisorResumenFilaSchema = z.object({
    franja: z.string(),
    franja_inicio: z.string(),
    valores: z.record(z.string(), z.number()),
    /**
     * Cantidad NO validada por columna en esta franja (`validado_at IS NULL`).
     * Una columna con `pendientes[key] > 0` tiene bloques sin validar en esa
     * franja → el FE la marca (ámbar) para que se vea qué no saldrá en la foto.
     * Columna ausente = 0. En el payload de la foto es 0/ausente.
     */
    pendientes: z.record(z.string(), z.number()),
    total: z.number(),
});
/** Un turno disponible en el scope del supervisor (para el selector). */
export const supervisorResumenTurnoSchema = z.object({
    numero: z.number().int(),
    nombre: z.string(),
    hora_inicio: z.string(),
    hora_fin: z.string(),
});
export const supervisorResumenHoraSchema = z.object({
    /** Fecha operativa del resumen (YYYY-MM-DD). */
    fecha: z.string(),
    /** Nombres de las plantas del scope del supervisor (para el subtítulo). */
    plantas: z.array(z.string()),
    /** Turnos disponibles en el scope, ordenados por número (para el selector). */
    turnos: z.array(supervisorResumenTurnoSchema),
    /**
     * Número de turno al que corresponde este payload. Lo resuelve el backend: si
     * no se pasó `turno_numero`, usa el turno programado del supervisor hoy y, en
     * su defecto, el primer turno disponible. `null` sólo si no hay turnos.
     */
    turno_numero: z.number().int().nullable(),
    /**
     * `true` cuando el payload trae SOLO registros validados por el supervisor
     * (`validado_at IS NOT NULL`). Es lo que alimenta la foto para compartir: lo
     * no validado no aparece — incentivo directo a validar.
     */
    solo_validados: z.boolean(),
    /** Nombre completo del usuario que consulta (para el pie de la foto). */
    supervisor_nombre: z.string(),
    /**
     * Meta acumulada del turno (suma de `meta_acumulada` de las líneas del scope
     * en `vw_kpis_linea_turno`). `null` si no hay capacidades configuradas. Las
     * corridas de tienda no tienen meta — el % de cumplimiento de la foto es
     * total_general/meta_total y puede incluir tiendas en el numerador.
     */
    meta_total: z.number().nullable(),
    /** Columnas del pivote, ordenadas (líneas por código, TIENDAS al final). */
    columnas: z.array(supervisorResumenColumnaSchema),
    /** Filas del pivote, ordenadas por franja ascendente. */
    filas: z.array(supervisorResumenFilaSchema),
    /** Suma total del turno (todas las columnas y franjas). */
    total_general: z.number(),
    /**
     * Unidad de total_general y meta_total: 'kg' solo si todo lo sumado es de
     * SKUs GRANEL; mezcla o vacío → 'paq'.
     */
    unidad: unidadMedidaSchema.optional(),
});
//# sourceMappingURL=supervisor_resumen_hora.js.map